import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useActorBoundary } from '@application/common/hooks/useActorBoundary';
import { useBoundRequest } from '@application/common/hooks/useBoundRequest';
import { FaqtoringApi } from '@infrastructure/faqtoring-api';
export function FaqtoringAvailabilitySwitch() {
    const { t } = useTranslation('faqtoring');
    const actor = useActorBoundary();
    const data = useBoundRequest(`faqtoring-availability:${actor.actor}`, () => actor.read(FaqtoringApi.availabilityAdmin));
    if (actor.changed)
        return _jsx(Alert, { severity: 'warning', children: t('accountChanged') });
    if (data.status === 'loading')
        return _jsx(Typography, { role: 'status', children: t('loading') });
    if (data.status === 'error' || !data.data)
        return _jsx(Alert, { severity: 'error', children: t('availability.loadError') });
    return _jsx(AvailabilityControl, { initial: data.data, checkActor: actor.check }, actor.actor);
}
export function AvailabilityControl({ initial, checkActor }) {
    const { t } = useTranslation('faqtoring');
    const [value, setValue] = useState(initial);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [uncertain, setUncertain] = useState(false);
    const locked = useRef(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const change = async (enabled) => {
        if (locked.current || uncertain || !checkActor())
            return;
        locked.current = true;
        setBusy(true);
        setMessage('');
        try {
            const saved = await FaqtoringApi.saveAvailability(enabled, value.revision);
            if (mounted.current && checkActor()) {
                setValue(saved);
                setMessage('saved');
            }
        }
        catch (error) {
            if (mounted.current && checkActor()) {
                const code = error?.response?.data?.code;
                setMessage(code === 'CONCURRENT_CHANGE_RELOAD' ? 'availability.conflict' : code === 'CONNECTION_NOT_CONFIGURED' ? 'availability.notConfigured' : 'availability.saveError');
                // The write might have succeeded before the response was lost. Read back, never
                // optimistically invert a financial setting or replay an uncertain write.
                try {
                    const latest = await FaqtoringApi.availabilityAdmin();
                    if (mounted.current && checkActor())
                        setValue(latest);
                }
                catch {
                    if (mounted.current && checkActor()) {
                        setUncertain(true);
                        setMessage('availability.unknownState');
                    }
                }
            }
        }
        finally {
            locked.current = false;
            if (mounted.current)
                setBusy(false);
        }
    };
    return _jsxs(Box, { sx: { my: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }, "aria-busy": busy, children: [_jsx(Typography, { variant: 'h6', children: t('availability.title') }), _jsx(FormControlLabel, { label: t(value.enabled ? 'availability.on' : 'availability.off'), control: _jsx(Switch, { inputProps: { 'aria-label': t('availability.title') }, checked: value.enabled, disabled: busy || uncertain || (!value.enabled && !value.connectionConfigured), onChange: event => void change(event.target.checked) }) }), _jsx(Typography, { children: t(value.effectiveEnabled ? 'availability.enabledHint' : 'availability.disabledHint') }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', sx: { mt: 1 }, children: t('availability.existingHint') }), !value.connectionConfigured && _jsx(Alert, { severity: 'warning', sx: { mt: 2 }, children: t('availability.notConfigured') }), message && _jsx(Alert, { severity: message === 'saved' ? 'success' : 'error', sx: { mt: 2 }, role: 'status', children: t(message) })] });
}
